/**
 * Badge "N planes" de la lista de contratantes, desplegable.
 *
 * Al hacer clic pide los planes de la suscripción a
 * `/api/pdf/trazabilidad-planes` y los muestra con su nombre y código de
 * certificado. La carga es **perezosa**: la lista solo trae el conteo, y pedir
 * los planes de todas las filas por adelantado serían N llamadas para un dato
 * que casi nunca se mira. Se cachea tras la primera apertura.
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';

const PlanesBadge = ({ contratacionId, numPlanes, codigosPrecargados = [] }) => {
  const { getToken } = useAuth();
  const [planes, setPlanes]   = useState(null);   // null = aún no cargado
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const cargar = useCallback(async () => {
    if (planes || loading) return;   // ya cargado o en curso
    setLoading(true);
    setError(null);
    try {
      const token = await getToken({ skipCache: true });
      const res = await fetch(`/api/pdf/trazabilidad-planes?id=${contratacionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `Error ${res.status}`);
      }
      const { planes: p } = await res.json();
      setPlanes(p || []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los planes');
    } finally {
      setLoading(false);
    }
  }, [contratacionId, getToken, planes, loading]);

  return (
    <Popover onOpenChange={(open) => { if (open) cargar(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // stopPropagation: la fila tiene su propio onClick (abrir contratante).
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
          title="Ver planes de esta suscripción"
        >
          {numPlanes === 1 ? '1 plan' : `${numPlanes} planes`}
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-3 py-2">
          <p className="text-sm font-medium">Planes de la suscripción</p>
          {/* Los códigos ya vienen en la fila (contratantesService los resuelve
              para que el buscador los alcance): se muestran de inmediato, sin
              esperar al fetch que trae los nombres de plan. */}
          {codigosPrecargados?.length > 0 && !planes && (
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {codigosPrecargados.join(' · ')}
            </p>
          )}
        </div>

        <div className="max-h-64 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando planes…
            </div>
          )}

          {error && (
            <p className="px-3 py-4 text-sm text-destructive">{error}</p>
          )}

          {!loading && !error && planes?.length === 0 && (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              No se encontraron planes.
            </p>
          )}

          {!loading && !error && planes?.map((p, i) => (
            <div
              key={p.contratacion_id || i}
              className="border-b px-3 py-2 last:border-b-0"
            >
              <p className="text-sm font-medium">{p.planNombre}</p>
              {p.productoNombre && (
                <p className="text-xs text-muted-foreground">{p.productoNombre}</p>
              )}
              <p className="mt-1 text-xs">
                {p.certificadoCodigo ? (
                  <span className="font-mono text-muted-foreground">
                    {p.certificadoCodigo}
                  </span>
                ) : (
                  // Un plan sin certificado aún no fue emitido: es un estado
                  // normal, no un error de carga.
                  <span className="italic text-muted-foreground">
                    Sin certificado emitido
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PlanesBadge;
